// import { formateObjKeys } from '@didi/trade-sdk/lib/quartz-mall'
/**
 * 格式化金额: 分到元
 * @param {number} fen - 金额
 */
export const formatFen2Yuan = fen =>
  fen === 0 ? fen : Math.round(fen * 100) / 10000

/**
 * 数字前面补 0
 * @param {number} value - 值
 * @param {number} [digital=2] - 要补到多少位
 * @return {string}
 */
export const fillZero = (value, digital = 2) => {
  const str = `${value}`
  let fill = ''
  while (str.length < digital) {
    fill += '0'
    digital--
  }
  return `${fill}${str}`
}

/**
 * 格式化给定时间戳为 时长 的时分秒字符串
 * @param {number} timestamp - 要格式化的时间戳
 */
export const timestamp2hms = timestamp => {
  const h = parseInt(timestamp / 1000 / 60 / 60)
  const m = parseInt((timestamp / 1000 / 60) % 60)
  const s = parseInt((timestamp / 1000) % 60)
  return {
    h: fillZero(h),
    m: fillZero(m),
    s: fillZero(s),
  }
}

/**
 * 时间戳格式化为年月日时分秒
 * @param {number|Date} timestamp - 时间戳 / 日期对象
 * @returns {Object}
 */
export const timestamp2ymdhms = timestamp => {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp)
  return {
    year: d.getFullYear(),
    month: fillZero(d.getMonth() + 1),
    date: fillZero(d.getDate()),
    hour: fillZero(d.getHours()),
    minute: fillZero(d.getMinutes()),
    second: fillZero(d.getSeconds()),
    day: d.getDay(),
    millisecond: d.getMilliseconds(),
  }
}

/**
 * 时间戳格式化为年月日时分秒
 * @param {number|Date} timestamp - 时间戳 / 日期对象
 * @returns {Object}
 */
export const timestampYmd = timestamp => {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp)
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    date: d.getDate(),
  }
}

/**
 * 下划线转驼峰
 * @param {string} str - 原字符串
 * @return {string}
 */
export const snakeCase2camelCase = str =>
  str.replace(/_(\w)/g, (_, letter) => letter.toUpperCase())

/**
 * 驼峰转下划线
 * @param {string} str - 原字符串
 * @return {string}
 */
export const camelCase2SnakeCase = str =>
  str.replace(/([A-Z])/g, '_$1').toLowerCase()

/**
 * 驼峰转全小写没有连接符
 * @param {string} str - 原字符串
 * @return {string}
 */
export const camelCase2dense = str =>
  str.replace(/([A-Z])/g, '$1').toLowerCase()

/**
 * 下划线转全小写没有连接符
 * @param {string} str - 原字符串
 * @return {string}
 */
export const snakeCase2dense = str => str.replace(/_/g, '')

/**
 * @typedef TimeString
 * @property {boolean} gt1day - 是否大于 1 天
 * @property {string} days - 天
 * @property {string} hours - 时
 * @property {string} minutes - 分
 * @property {string} seconds - 秒
 */

const MillisecondsOf1Day = 86400000 // 24 * 60 * 60 * 1000

/**
 * 毫秒数转倒计时文字
 * @param {number} time - 时间戳
 * @return {TimeString}
 */
export const timestampToString = timestamp => {
  const gt1day = timestamp > MillisecondsOf1Day
  const days = gt1day ? Math.floor(timestamp / MillisecondsOf1Day) : 0
  // const restMilliseconds = timestamp % MillisecondsOf1Day
  // const d = new Date(restMilliseconds)
  // const hours = d.getHours() - 8
  // const minutes = d.getMinutes()
  // const seconds = d.getSeconds()
  const hours = Math.floor((timestamp / 1000 / 60 / 60) % 24)
  const minutes = Math.floor((timestamp / 1000 / 60) % 60)
  const seconds = Math.floor((timestamp / 1000) % 60)
  return {
    gt1day,
    days,
    hours,
    minutes,
    seconds,
  }
}

/**
 * 单位转化rpx 转化为vw
 * @param {string} css 样式
 * @returns rpx
 */
export function rpx(css) {
  const designWidth = 750
  const ratio = `${100 / designWidth}`
  const unit = 'vw'
  return css?.replace(/(\d*\.?\d+)rpx([\s;,)}]|$)/g, function (...arg) {
    return `${arg[1] * ratio}${unit}${arg[2]}`
  })
}

export function trim(str) {
  if (typeof str === 'object') {
    return str
  }
  if (typeof str === 'number') {
    return str
  }
  return str?.trim?.() || ''
}

/**
 * 代理数据 将驼峰转化为下划线
 * @param {*} obj 数据
 * @returns
 */
export function proxySnakeCase(obj) {
  return new Proxy(obj, {
    get(target, prop, receiver) {
      if (target[prop] !== undefined) {
        return target[prop]
      } else {
        const snakeProp = snakeCase2camelCase(prop)
        if (target[snakeProp] !== undefined) {
          return target[snakeProp]
        } else {
          const snakePath = snakeProp.split('.')
          let current = target
          for (const part of snakePath) {
            current = current[part]
            if (current === undefined) break
          }
          return current
        }
      }
    },
  })
}

/**
 * 代理数据 将驼峰代理为下划线，访问goodsInfo实际使用为goods_info
 * @param {*} obj 数据
 * @returns
 */
export function proxyCamelCase(obj) {
  return new Proxy(obj, {
    get(target, prop, receiver) {
      if (target[prop] !== undefined) {
        return target[prop]
      } else {
        const camelProp = camelCase2SnakeCase(prop)
        if (target[camelProp] !== undefined) {
          return target[camelProp]
        } else {
          const camelPath = camelProp.split('.')
          let current = target
          for (const part of camelPath) {
            current = current[part]
            if (current === undefined) break
          }
          return current
        }
      }
    },
  })
}
